import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    createPayoutCascadesUrl,
    createPayoutCascadeTemplateUrl,
    isNewPayoutCascadeEntityId,
    type PayoutGroup,
    useCreatePayoutCascadeTemplate,
    useDeletePayoutCascadeTemplate,
    usePayoutCascadeGroupsList,
    usePayoutCascadeTemplate,
    useUpdatePayoutCascadeTemplate,
} from '~/entities/payout';
import { type OrderedEntry } from '~/shared/ui/OrderedEntryList';

export interface TemplateGroupEntryValue {
    id: string;
    name: string;
}

const createEntryKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useCascadeTemplateEditor = (id?: string) => {
    const navigate = useNavigate();
    const isNew = isNewPayoutCascadeEntityId(id);

    const template = usePayoutCascadeTemplate({ 'id.value': id ?? '' });
    const groups = usePayoutCascadeGroupsList({
        'pagination.page': '0',
        'pagination.perPage': '1000',
    });

    const [name, setName] = useState('');
    const [entries, setEntries] = useState<OrderedEntry<TemplateGroupEntryValue>[]>([]);
    const [isLoaded, setIsLoaded] = useState(isNew);

    useEffect(() => {
        if (isLoaded || !template.data?.template) {
            return;
        }

        setName(template.data.template.name?.value ?? '');
        setEntries(
            (template.data.template.groups ?? []).map((group, index) => ({
                key: `${group.id?.value ?? index}-${index}`,
                value: {
                    id: group.id?.value ?? '',
                    name: group.name?.value || group.id?.value || '',
                },
            })),
        );
        setIsLoaded(true);
    }, [isLoaded, template.data?.template]);

    const groupOptions = (groups.data?.items ?? [])
        .filter((group): group is PayoutGroup & { id: { value: string } } => Boolean(group.id?.value))
        .map((group) => ({
            value: group.id.value,
            label: group.name?.value || group.id.value,
        }));

    const groupLabelById = new Map(groupOptions.map((option) => [option.value, option.label]));

    const create = useCreatePayoutCascadeTemplate();

    const update = useUpdatePayoutCascadeTemplate();

    const deleteTemplate = useDeletePayoutCascadeTemplate({
        onSuccess: () => navigate(createPayoutCascadesUrl('templates')),
    });

    const handleAddGroup = (groupId: string) => {
        const label = groupLabelById.get(groupId) ?? groupId;

        setEntries((prev) => [...prev, { key: createEntryKey(), value: { id: groupId, name: label } }]);
    };

    const handleClear = () => setEntries([]);

    const isValid = name.trim().length > 0 && entries.length > 0;

    const handleSubmit = () => {
        const groupIds = entries.map((entry) => ({ value: entry.value.id }));

        if (isNew) {
            create.mutate(
                { name: { value: name }, groupIds },
                {
                    onSuccess: (response) => {
                        const newId = response.id?.value;

                        if (newId) {
                            navigate(createPayoutCascadeTemplateUrl(newId), { replace: true });
                        }
                    },
                },
            );

            return;
        }

        update.mutate({ id: { value: id }, name: { value: name }, groupIds });
    };

    const handleDelete = () => {
        if (!id) {
            return;
        }

        deleteTemplate.mutate({ 'id.value': id });
    };

    return {
        isNew,
        isLoading: !isNew && template.isPending,
        name,
        setName,
        entries,
        setEntries,
        groupOptions,
        isGroupOptionsLoading: groups.isPending,
        handleAddGroup,
        handleClear,
        isValid,
        handleSubmit,
        handleDelete,
        isSubmitPending: create.isPending || update.isPending,
        isDeletePending: deleteTemplate.isPending,
    };
};
